import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import {
  Grid,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  FormGroup,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
} from '@mui/material';

import MultiSelect from '../../common/multi-select';
import DateRangeFilter from '../../common/date-range-filters/date-range-filter';
import {
  pickupTaskReasonNames,
  filterInspectTaskReasonNames,
  PICKUP_FIELDS,
  scooterTypeValues,
  LinkAdminVehicleZoneCategories,
  VehicleZoneCategories,
  locationFilterFieldsValues,
  maintenanceFilterFieldsValues,
  hasDefaultGpkgFilterFieldsValues,
} from '../../../lib/utils';
import { modalStyles } from '../../../lib/styles';

import {
  PICKUP_FIELDS_FILTER,
  FILTERS,
  FLEET,
  MAINTENANCE,
  HAS_DEFAULT_GPKG,
  MIN_BATTERY,
  MAX_BATTERY,
  TASK_ASSIGNEES,
  PICKUP_REASONS,
  PICKUP_ZONES,
  GEOFENCE_LAYERS,
  INSPECT_REASONS,
  HARDWARE,
  LAST_PICKUP_BEFORE,
  LAST_PICKUP_AFTER,
  LAST_DEPLOY_BEFORE,
  LAST_DEPLOY_AFTER,
  LAST_TRIP_BEFORE,
  LAST_TRIP_AFTER,
  LAST_COMMUNICATION_BEFORE,
  LAST_COMMUNICATION_AFTER,
  LAST_RIDER_SCAN_BEFORE,
  LAST_RIDER_SCAN_AFTER,
  SCOOTER_TYPE,
  MIN_SEARCH_ATTEMPTS,
  MAX_SEARCH_ATTEMPTS,
  LAST_REPORTED_NETWORK,
  LOCATION,
} from '../../../lib/filter-utils';

const ASSIGN_OPTION = 'Anyone';

function LinkTableFiltersModal({
  open,
  onClose,
  onChange,
  pickupZones,
  childMarkets,
  cellNetworks,
  filters,
  defaultFilters,
  wranglerOptions,
}) {
  const [tableFilters, setTableFilters] = useState(defaultFilters);
  const [pickupFieldsFilter, setPickupFieldsFilter] = useState(
    tableFilters[PICKUP_FIELDS_FILTER],
  );
  const [maintenanceFilter, setMaintenanceFilter] = useState(tableFilters[MAINTENANCE]);
  const [hasDefaultGpkgFilter, setHasDefaultGpkgFilter] = useState(
    tableFilters[HAS_DEFAULT_GPKG],
  );
  const [minBatteryFilter, setMinBattery] = useState(tableFilters[MIN_BATTERY]);
  const [maxBatteryFilter, setMaxBattery] = useState(tableFilters[MAX_BATTERY]);
  const [minSearchAttemptsFilter, setMinSearchAttempts] = useState(
    tableFilters[MIN_SEARCH_ATTEMPTS],
  );
  const [maxSearchAttemptsFilter, setMaxSearchAttempts] = useState(
    tableFilters[MAX_SEARCH_ATTEMPTS],
  );
  const [fleetFilter, setFleetFilter] = useState(tableFilters[FLEET]);
  const [taskAssigneesFilter, setTaskAssigneesFilter] = useState(tableFilters[TASK_ASSIGNEES]);
  const [pickupReasonsFilter, setPickupReasonsFilter] = useState(tableFilters[PICKUP_REASONS]);
  const [pickupZonesFilter, setPickupZonesFilter] = useState(tableFilters[PICKUP_ZONES]);
  const [locationFilter, setLocationFilter] = useState(tableFilters[LOCATION]);
  const [geofenceLayersFilter, setGeofenceLayersFilter] = useState(
    tableFilters[GEOFENCE_LAYERS],
  );
  const [inspectReasonsFilter, setInspectReasonsFilter] = useState(
    tableFilters[INSPECT_REASONS],
  );
  const [hardwareFilter, setHardwareFilter] = useState(tableFilters[HARDWARE]);
  const [scooterTypeFilter, setScooterTypeFilter] = useState(tableFilters[SCOOTER_TYPE]);
  const [lastPickupFilterBefore, setLastPickupFilterBefore] = useState(
    tableFilters[LAST_PICKUP_BEFORE],
  );
  const [lastPickupFilterAfter, setLastPickupFilterAfter] = useState(
    tableFilters[LAST_PICKUP_AFTER],
  );
  const [lastDeployFilterBefore, setLastDeployFilterBefore] = useState(
    tableFilters[LAST_DEPLOY_BEFORE],
  );
  const [lastDeployFilterAfter, setLastDeployFilterAfter] = useState(
    tableFilters[LAST_DEPLOY_AFTER],
  );
  const [lastTripFilterBefore, setLastTripFilterBefore] = useState(
    tableFilters[LAST_TRIP_BEFORE],
  );
  const [lastTripFilterAfter, setLastTripFilterAfter] = useState(tableFilters[LAST_TRIP_AFTER]);
  const [lastCommunicationFilterBefore, setLastCommunicationFilterBefore] = useState(
    tableFilters[LAST_COMMUNICATION_BEFORE],
  );
  const [lastCommunicationFilterAfter, setLastCommunicationFilterAfter] = useState(
    tableFilters[LAST_COMMUNICATION_AFTER],
  );
  const [lastRiderScanFilterBefore, setLastRiderScanFilterBefore] = useState(
    tableFilters[LAST_RIDER_SCAN_BEFORE],
  );
  const [lastRiderScanFilterAfter, setLastRiderScanFilterAfter] = useState(
    tableFilters[LAST_RIDER_SCAN_AFTER],
  );
  const [lastReportedNetworkFilter, setLastReportedNetworkFilter] = useState(
    tableFilters[LAST_REPORTED_NETWORK],
  );

  const sendUpdates = (updatedField, updatedValue) => {
    const stateUpdates = {
      ...tableFilters,
      [PICKUP_FIELDS_FILTER]: pickupFieldsFilter,
      [MAINTENANCE]: maintenanceFilter,
      [HAS_DEFAULT_GPKG]: hasDefaultGpkgFilter,
      [MIN_BATTERY]: minBatteryFilter,
      [MAX_BATTERY]: maxBatteryFilter,
      [FLEET]: fleetFilter,
      [TASK_ASSIGNEES]: taskAssigneesFilter,
      [PICKUP_REASONS]: pickupReasonsFilter,
      [PICKUP_ZONES]: pickupZonesFilter,
      [GEOFENCE_LAYERS]: geofenceLayersFilter,
      [HARDWARE]: hardwareFilter,
      [INSPECT_REASONS]: inspectReasonsFilter,
      [SCOOTER_TYPE]: scooterTypeFilter,
      [LAST_PICKUP_BEFORE]: lastPickupFilterBefore,
      [LAST_PICKUP_AFTER]: lastPickupFilterAfter,
      [LAST_DEPLOY_BEFORE]: lastDeployFilterBefore,
      [LAST_DEPLOY_AFTER]: lastDeployFilterAfter,
      [LAST_TRIP_BEFORE]: lastTripFilterBefore,
      [LAST_TRIP_AFTER]: lastTripFilterAfter,
      [LAST_COMMUNICATION_BEFORE]: lastCommunicationFilterBefore,
      [LAST_COMMUNICATION_AFTER]: lastCommunicationFilterAfter,
      [LAST_RIDER_SCAN_BEFORE]: lastRiderScanFilterBefore,
      [LAST_RIDER_SCAN_AFTER]: lastRiderScanFilterAfter,
      [LAST_REPORTED_NETWORK]: lastReportedNetworkFilter,
      [MIN_SEARCH_ATTEMPTS]: minSearchAttemptsFilter,
      [MAX_SEARCH_ATTEMPTS]: maxSearchAttemptsFilter,
      [LOCATION]: locationFilter,
    };

    if (updatedField) stateUpdates[updatedField] = updatedValue;
    setTableFilters(stateUpdates);
    onChange(stateUpdates);
  };

  const clearDateFilters = (updatedBefore, updatedAfter, beforeFn, afterFn) => {
    beforeFn(undefined);
    afterFn(undefined);

    const stateUpdates = {
      ...tableFilters,
      [PICKUP_FIELDS_FILTER]: pickupFieldsFilter,
      [MAINTENANCE]: maintenanceFilter,
      [HAS_DEFAULT_GPKG]: hasDefaultGpkgFilter,
      [MIN_BATTERY]: minBatteryFilter,
      [MAX_BATTERY]: maxBatteryFilter,
      [FLEET]: fleetFilter,
      [TASK_ASSIGNEES]: taskAssigneesFilter,
      [PICKUP_REASONS]: pickupReasonsFilter,
      [PICKUP_ZONES]: pickupZonesFilter,
      [LOCATION]: locationFilter,
      [GEOFENCE_LAYERS]: geofenceLayersFilter,
      [HARDWARE]: hardwareFilter,
      [INSPECT_REASONS]: inspectReasonsFilter,
      [SCOOTER_TYPE]: scooterTypeFilter,
      [LAST_PICKUP_BEFORE]: lastPickupFilterBefore,
      [LAST_PICKUP_AFTER]: lastPickupFilterAfter,
      [LAST_DEPLOY_BEFORE]: lastDeployFilterBefore,
      [LAST_DEPLOY_AFTER]: lastDeployFilterAfter,
      [LAST_TRIP_BEFORE]: lastTripFilterBefore,
      [LAST_TRIP_AFTER]: lastTripFilterAfter,
      [LAST_COMMUNICATION_BEFORE]: lastCommunicationFilterBefore,
      [LAST_COMMUNICATION_AFTER]: lastCommunicationFilterAfter,
      [LAST_RIDER_SCAN_BEFORE]: lastRiderScanFilterBefore,
      [LAST_RIDER_SCAN_AFTER]: lastRiderScanFilterAfter,
      [LAST_REPORTED_NETWORK]: lastReportedNetworkFilter,
      [MIN_SEARCH_ATTEMPTS]: minSearchAttemptsFilter,
      [MAX_SEARCH_ATTEMPTS]: maxSearchAttemptsFilter,
    };

    if (updatedBefore) stateUpdates[updatedBefore] = undefined;
    if (updatedAfter) stateUpdates[updatedAfter] = undefined;
    setTableFilters(stateUpdates);
    onChange(stateUpdates);
  };

  const updateTableFilters = () => {
    const stateUpdates = {
      ...tableFilters,
      [PICKUP_FIELDS_FILTER]: filters.pickupFieldsFilter,
      [MAINTENANCE]: filters.maintenanceFilter,
      [HAS_DEFAULT_GPKG]: filters.hasDefaultGpkgFilter,
      [MIN_BATTERY]: filters.minBatteryFilter,
      [MAX_BATTERY]: filters.maxBatteryFilter,
      [FLEET]: filters.fleetFilter,
      [TASK_ASSIGNEES]: filters.taskAssigneesFilter,
      [PICKUP_REASONS]: filters.pickupReasonsFilter,
      [PICKUP_ZONES]: filters.pickupZonesFilter,
      [LOCATION]: filters.locationFilter,
      [GEOFENCE_LAYERS]: filters.geofenceLayersFilter,
      [HARDWARE]: filters.hardwareFilter,
      [INSPECT_REASONS]: filters.inspectReasonsFilter,
      [SCOOTER_TYPE]: filters.scooterTypeFilter,
      [LAST_PICKUP_BEFORE]: filters.lastPickupFilterBefore,
      [LAST_PICKUP_AFTER]: filters.lastPickupFilterAfter,
      [LAST_DEPLOY_BEFORE]: filters.lastDeployFilterBefore,
      [LAST_DEPLOY_AFTER]: filters.lastDeployFilterAfter,
      [LAST_TRIP_BEFORE]: filters.lastTripFilterBefore,
      [LAST_TRIP_AFTER]: filters.lastTripFilterAfter,
      [LAST_COMMUNICATION_BEFORE]: filters.lastCommunicationFilterBefore,
      [LAST_COMMUNICATION_AFTER]: filters.lastCommunicationFilterAfter,
      [LAST_RIDER_SCAN_BEFORE]: filters.lastRiderScanFilterBefore,
      [LAST_RIDER_SCAN_AFTER]: filters.lastRiderScanFilterAfter,
      [LAST_REPORTED_NETWORK]: filters.lastReportedNetworkFilter,
      [MIN_SEARCH_ATTEMPTS]: filters.minSearchAttemptsFilter,
      [MAX_SEARCH_ATTEMPTS]: filters.maxSearchAttemptsFilter,
    };
    setTableFilters(stateUpdates);
    setPickupFieldsFilter(stateUpdates.pickupFieldsFilter);
    setMaintenanceFilter(stateUpdates.maintenanceFilter);
    setHasDefaultGpkgFilter(stateUpdates.hasDefaultGpkgFilter);
    setMinBattery(stateUpdates.minBatteryFilter);
    setMaxBattery(stateUpdates.maxBatteryFilter);
    setMinSearchAttempts(stateUpdates.minSearchAttemptsFilter);
    setMaxSearchAttempts(stateUpdates.maxSearchAttemptsFilter);
    setFleetFilter(stateUpdates.fleetFilter);
    setTaskAssigneesFilter(stateUpdates.taskAssigneesFilter);
    setPickupReasonsFilter(stateUpdates.pickupReasonsFilter);
    setPickupZonesFilter(stateUpdates.pickupZonesFilter);
    setLocationFilter(stateUpdates.locationFilter);
    setGeofenceLayersFilter(stateUpdates.geofenceLayersFilter);
    setInspectReasonsFilter(stateUpdates.inspectReasonsFilter);
    setHardwareFilter(stateUpdates.hardwareFilter);
    setScooterTypeFilter(stateUpdates.scooterTypeFilter);
    setLastPickupFilterBefore(stateUpdates.lastPickupFilterBefore);
    setLastPickupFilterAfter(stateUpdates.lastPickupFilterAfter);
    setLastDeployFilterBefore(stateUpdates.lastDeployFilterBefore);
    setLastDeployFilterAfter(stateUpdates.lastDeployFilterAfter);
    setLastTripFilterBefore(stateUpdates.lastTripFilterBefore);
    setLastTripFilterAfter(stateUpdates.lastTripFilterAfter);
    setLastCommunicationFilterBefore(stateUpdates.lastCommunicationFilterBefore);
    setLastCommunicationFilterAfter(stateUpdates.lastCommunicationFilterAfter);
    setLastRiderScanFilterBefore(stateUpdates.lastRiderScanFilterBefore);
    setLastRiderScanFilterAfter(stateUpdates.lastRiderScanFilterAfter);
    setLastReportedNetworkFilter(stateUpdates.lastReportedNetworkFilter);
    onChange(stateUpdates);
  };

  useEffect(() => {
    if (isEmpty(defaultFilters) || isEmpty(filters)
      || isEqual(filters, defaultFilters)) return;
    sendUpdates();
  }, []);

  useEffect(() => {
    if (!isEmpty(filters) && !isEqual(filters, tableFilters)) {
      updateTableFilters();
    }
  }, [filters, tableFilters]);

  const updateFilters = (value, name, setFn) => {
    setFn(value);
    sendUpdates(name, value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{ sx: modalStyles.dialog }}
      disableEnforceFocus
    >
      <DialogTitle>
        <Typography variant="h6" component="span">Advanced Filters</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container>
          <RadioGroup
            row
            value={pickupFieldsFilter}
            onChange={
              (event) => updateFilters(
                event.target.value,
                FILTERS[PICKUP_FIELDS_FILTER].NAME,
                (val) => setPickupFieldsFilter(val),
              )
            }
          >
            <FormControlLabel
              label={<FormLabel>All Scooters</FormLabel>}
              labelPlacement="end"
              value={PICKUP_FIELDS.ALL_VEHICLES}
              control={<Radio />}
            />
            <FormControlLabel
              label={<FormLabel>All Pickups</FormLabel>}
              labelPlacement="end"
              value={PICKUP_FIELDS.ALL_PICKUPS}
              control={<Radio />}
            />
            <FormControlLabel
              label={<FormLabel>Urgent Pickups Only</FormLabel>}
              labelPlacement="end"
              value={PICKUP_FIELDS.URGENT_ONLY}
              control={<Radio />}
            />
            <FormControlLabel
              label={<FormLabel>Not For Pickup</FormLabel>}
              labelPlacement="end"
              value={PICKUP_FIELDS.NOT_FOR_PICKUP}
              control={<Radio />}
            />
            <FormControlLabel
              label={<FormLabel>All Pickups And Inspects</FormLabel>}
              labelPlacement="end"
              value={PICKUP_FIELDS.ALL_PICKUPS_INSPECTS}
              control={<Radio />}
            />
          </RadioGroup>
          <Grid spacing={{ xs: 0, md: 2 }} item container alignItems="flex-start" direction="row" sx={{ mt: 2 }}>
            <Grid spacing={2} item container xs={12} md={6}>
              {childMarkets.length > 1 && (
                <Grid item xs={12}>
                  <Grid item sx={modalStyles.relative}>
                    <FormLabel sx={modalStyles.label}>Fleet</FormLabel>
                    <MultiSelect
                      placeholder="Select a fleet"
                      value={fleetFilter}
                      options={childMarkets.map((market) => ({
                        value: market._id, label: market.name,
                      }))}
                      handleChange={
                        (value) => updateFilters(
                          value,
                          FILTERS[FLEET].NAME,
                          (val) => setFleetFilter(val),
                        )
                      }
                    />
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                <Grid item sx={modalStyles.relative}>
                  <FormLabel sx={modalStyles.label}>Task Assignment</FormLabel>
                  <MultiSelect
                    placeholder="Select assignee(s)"
                    value={taskAssigneesFilter}
                    options={[
                      { value: ASSIGN_OPTION, label: ASSIGN_OPTION },
                    ].concat(wranglerOptions)}
                    handleChange={
                      (value) => updateFilters(
                        value,
                        FILTERS[TASK_ASSIGNEES].NAME,
                        (val) => setTaskAssigneesFilter(val),
                      )
                    }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid item sx={modalStyles.relative}>
                  <FormLabel sx={modalStyles.label}>Pickup Reason</FormLabel>
                  <MultiSelect
                    placeholder="Select a pickup reason"
                    value={pickupReasonsFilter}
                    options={pickupTaskReasonNames.map((reason) => ({
                      value: reason, label: reason,
                    }))}
                    handleChange={
                      (value) => updateFilters(
                        value,
                        FILTERS[PICKUP_REASONS].NAME,
                        (val) => setPickupReasonsFilter(val),
                      )
                    }
                    isDisabled={pickupFieldsFilter === PICKUP_FIELDS.NOT_FOR_PICKUP}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid item sx={modalStyles.relative}>
                  <FormLabel sx={modalStyles.label}>Zones</FormLabel>
                  <MultiSelect
                    placeholder="Select a zone"
                    value={pickupZonesFilter}
                    options={pickupZones.map((zone) => ({ value: zone._id, label: zone.name }))}
                    handleChange={
                      (value) => updateFilters(
                        value,
                        FILTERS[PICKUP_ZONES].NAME,
                        (val) => setPickupZonesFilter(val),
                      )
                    }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid item sx={modalStyles.relative}>
                  <FormLabel sx={modalStyles.label}>Location</FormLabel>
                  <MultiSelect
                    isSingle
                    placeholder="Select"
                    value={[locationFilter]}
                    options={locationFilterFieldsValues.map(
                      (option) => ({ label: option, value: option }),
                    )}
                    handleChange={
                        (value) => updateFilters(
                          value,
                          FILTERS[LOCATION].NAME,
                          (val) => setLocationFilter(val),
                        )
                      }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ display: 'none' }}>
                <Grid item sx={modalStyles.relative}>
                  <FormLabel sx={modalStyles.label}>Geofence Layers</FormLabel>
                  <MultiSelect
                    placeholder="Select a geofence layer"
                    value={geofenceLayersFilter}
                    options={Object.keys(LinkAdminVehicleZoneCategories)
                      .filter((c) => LinkAdminVehicleZoneCategories[c]
                        !== LinkAdminVehicleZoneCategories.PICKUP)
                      .map((c) => ({
                        value: VehicleZoneCategories[c],
                        label: LinkAdminVehicleZoneCategories[c],
                      }))}
                    handleChange={
                      (value) => updateFilters(
                        value,
                        FILTERS[GEOFENCE_LAYERS].NAME,
                        (val) => setGeofenceLayersFilter(val),
                      )
                    }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid item sx={modalStyles.relative}>
                  <FormLabel sx={modalStyles.label}>Inspect Reason</FormLabel>
                  <MultiSelect
                    placeholder="Select an inspect reason"
                    value={inspectReasonsFilter}
                    options={filterInspectTaskReasonNames.map((reason) => ({
                      value: reason, label: reason,
                    }))}
                    handleChange={
                      (value) => updateFilters(
                        value,
                        FILTERS[INSPECT_REASONS].NAME,
                        (val) => setInspectReasonsFilter(val),
                      )
                    }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid item sx={modalStyles.relative}>
                  <FormLabel sx={modalStyles.label}>Hardware</FormLabel>
                  <MultiSelect
                    placeholder="Select a hardware"
                    value={hardwareFilter}
                    options={[{ value: 'Seat', label: 'Seat' }]}
                    handleChange={
                      (value) => updateFilters(
                        value,
                        FILTERS[HARDWARE].NAME,
                        (val) => setHardwareFilter(val),
                      )
                    }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid item sx={modalStyles.relative}>
                  <FormLabel sx={modalStyles.label}>Scooter Type</FormLabel>
                  <MultiSelect
                    placeholder="Select a scooter type"
                    value={scooterTypeFilter}
                    options={scooterTypeValues.map((type) => ({ value: type, label: type }))}
                    handleChange={
                      (value) => updateFilters(
                        value,
                        FILTERS[SCOOTER_TYPE].NAME,
                        (val) => setScooterTypeFilter(val),
                      )
                    }
                  />
                </Grid>
              </Grid>
              {cellNetworks.length > 0 && (
                <Grid item xs={12}>
                  <Grid item sx={modalStyles.relative}>
                    <FormLabel sx={modalStyles.label}>Cellular Network</FormLabel>
                    <MultiSelect
                      placeholder="Select a cellular network"
                      value={lastReportedNetworkFilter}
                      options={cellNetworks.map((network) => ({
                        value: network, label: network,
                      }))}
                      handleChange={
                        (value) => updateFilters(
                          value,
                          FILTERS[LAST_REPORTED_NETWORK].NAME,
                          (val) => setLastReportedNetworkFilter(val),
                        )
                      }
                    />
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                <FormLabel>Battery Level (%)</FormLabel>
                <FormGroup row sx={{ mt: 1 }}>
                  <TextField
                    label="min"
                    value={minBatteryFilter}
                    onChange={
                      (event) => updateFilters(
                        event.target.value,
                        FILTERS[MIN_BATTERY].NAME,
                        (val) => setMinBattery(val),
                      )
                    }
                    style={{ width: '47.5%', marginRight: '5%' }}
                  />
                  <TextField
                    label="max"
                    value={maxBatteryFilter}
                    onChange={
                      (event) => updateFilters(
                        event.target.value,
                        FILTERS[MAX_BATTERY].NAME,
                        (val) => setMaxBattery(val),
                      )
                    }
                    style={{ width: '47.5%' }}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                <FormLabel>Search Attempts</FormLabel>
                <FormGroup row sx={{ mt: 1 }}>
                  <TextField
                    label="min"
                    value={minSearchAttemptsFilter}
                    onChange={
                      (event) => updateFilters(
                        event.target.value,
                        FILTERS[MIN_SEARCH_ATTEMPTS].NAME,
                        (val) => setMinSearchAttempts(val),
                      )
                    }
                    style={{ width: '47.5%', marginRight: '5%' }}
                  />
                  <TextField
                    label="max"
                    value={maxSearchAttemptsFilter}
                    onChange={
                      (event) => updateFilters(
                        event.target.value,
                        FILTERS[MAX_SEARCH_ATTEMPTS].NAME,
                        (val) => setMaxSearchAttempts(val),
                      )
                    }
                    style={{ width: '47.5%' }}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                <Grid item sx={modalStyles.relative}>
                  <FormLabel sx={modalStyles.label}>Needs maintenance</FormLabel>
                  <MultiSelect
                    isSingle
                    placeholder="Select"
                    value={[maintenanceFilter]}
                    options={maintenanceFilterFieldsValues.map(
                      (option) => ({ label: option, value: option }),
                    )}
                    handleChange={
                      (value) => updateFilters(
                        value,
                        FILTERS[MAINTENANCE].NAME,
                        (val) => setMaintenanceFilter(val),
                      )
                    }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid item sx={modalStyles.relative}>
                  <FormLabel sx={modalStyles.label}>
                    Geofence package
                  </FormLabel>
                  <MultiSelect
                    placeholder="Select"
                    value={hasDefaultGpkgFilter}
                    options={hasDefaultGpkgFilterFieldsValues.map(
                      (option) => ({ label: option, value: option }),
                    )}
                    handleChange={
                        (value) => updateFilters(
                          value,
                          FILTERS[HAS_DEFAULT_GPKG].NAME,
                          (val) => setHasDefaultGpkgFilter(val),
                        )
                      }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6} sx={{ ml: { xs: '-11px', md: 0 } }}>
              <Grid item xs={12}>
                <DateRangeFilter
                  name="Last Pickup"
                  onChangeBefore={
                    (data) => updateFilters(
                      data,
                      FILTERS[LAST_PICKUP_BEFORE].NAME,
                      (val) => setLastPickupFilterBefore(val),
                    )
                  }
                  onChangeAfter={
                    (data) => updateFilters(
                      data,
                      FILTERS[LAST_PICKUP_AFTER].NAME,
                      (val) => setLastPickupFilterAfter(val),
                    )
                  }
                  onClear={
                    () => clearDateFilters(
                      FILTERS[LAST_PICKUP_BEFORE].NAME,
                      FILTERS[LAST_PICKUP_AFTER].NAME,
                      setLastPickupFilterBefore,
                      setLastPickupFilterAfter,
                    )
                  }
                  beforeDate={lastPickupFilterBefore}
                  afterDate={lastPickupFilterAfter}
                />
              </Grid>
              <Grid item xs={12}>
                <DateRangeFilter
                  name="Last Deploy"
                  onChangeBefore={
                    (data) => updateFilters(
                      data,
                      FILTERS[LAST_DEPLOY_BEFORE].NAME,
                      (val) => setLastDeployFilterBefore(val),
                    )
                  }
                  onChangeAfter={
                    (data) => updateFilters(
                      data,
                      FILTERS[LAST_DEPLOY_AFTER].NAME,
                      (val) => setLastDeployFilterAfter(val),
                    )
                  }
                  onClear={
                    () => clearDateFilters(
                      FILTERS[LAST_DEPLOY_BEFORE].NAME,
                      FILTERS[LAST_DEPLOY_AFTER].NAME,
                      setLastDeployFilterBefore,
                      setLastDeployFilterAfter,
                    )
                  }
                  beforeDate={lastDeployFilterBefore}
                  afterDate={lastDeployFilterAfter}
                />
              </Grid>
              <Grid item xs={12}>
                <DateRangeFilter
                  name="Last Trip"
                  onChangeBefore={
                    (data) => updateFilters(
                      data,
                      FILTERS[LAST_TRIP_BEFORE].NAME,
                      (val) => setLastTripFilterBefore(val),
                    )
                  }
                  onChangeAfter={
                    (data) => updateFilters(
                      data,
                      FILTERS[LAST_TRIP_AFTER].NAME,
                      (val) => setLastTripFilterAfter(val),
                    )
                  }
                  onClear={
                    () => clearDateFilters(
                      FILTERS[LAST_TRIP_BEFORE].NAME,
                      FILTERS[LAST_TRIP_AFTER].NAME,
                      setLastTripFilterBefore,
                      setLastTripFilterAfter,
                    )
                  }
                  beforeDate={lastTripFilterBefore}
                  afterDate={lastTripFilterAfter}
                />
              </Grid>
              <Grid item xs={12}>
                <DateRangeFilter
                  name="Last Communication"
                  onChangeBefore={
                    (data) => updateFilters(
                      data,
                      FILTERS[LAST_COMMUNICATION_BEFORE].NAME,
                      (val) => setLastCommunicationFilterBefore(val),
                    )
                  }
                  onChangeAfter={
                    (data) => updateFilters(
                      data,
                      FILTERS[LAST_COMMUNICATION_AFTER].NAME,
                      (val) => setLastCommunicationFilterAfter(val),
                    )
                  }
                  onClear={
                    () => clearDateFilters(
                      FILTERS[LAST_COMMUNICATION_BEFORE].NAME,
                      FILTERS[LAST_COMMUNICATION_AFTER].NAME,
                      setLastCommunicationFilterBefore,
                      setLastCommunicationFilterAfter,
                    )
                  }
                  beforeDate={lastCommunicationFilterBefore}
                  afterDate={lastCommunicationFilterAfter}
                />
              </Grid>
              <Grid item xs={12}>
                <DateRangeFilter
                  name="Last Rider Scan"
                  onChangeBefore={
                    (data) => updateFilters(
                      data,
                      FILTERS[LAST_RIDER_SCAN_BEFORE].NAME,
                      (val) => setLastRiderScanFilterBefore(val),
                    )
                  }
                  onChangeAfter={
                    (data) => updateFilters(
                      data,
                      FILTERS[LAST_RIDER_SCAN_AFTER].NAME,
                      (val) => setLastRiderScanFilterAfter(val),
                    )
                  }
                  onClear={
                    () => clearDateFilters(
                      FILTERS[LAST_RIDER_SCAN_BEFORE].NAME,
                      FILTERS[LAST_RIDER_SCAN_AFTER].NAME,
                      setLastRiderScanFilterBefore,
                      setLastRiderScanFilterAfter,
                    )
                  }
                  beforeDate={lastRiderScanFilterBefore}
                  afterDate={lastRiderScanFilterAfter}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

LinkTableFiltersModal.propTypes = {
  onChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  childMarkets: PropTypes.array.isRequired,
  wranglerOptions: PropTypes.array.isRequired,
};

export default LinkTableFiltersModal;
